import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCube,
  IconFileDescription,
  IconFlask,
  IconLock,
  IconMailForward,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import {
  CommercialApiError,
  createSupplier,
  decideApproval,
  loadCommercialWorkspace,
  syntheticSend,
  updateSupplier,
} from "./api";
import type { CreateSupplierRequest, UpdateSupplierRequest } from "./api";
import type {
  ApprovalRequest,
  CommercialTab,
  CommercialWorkspaceData,
  CustomerQuote,
  Supplier,
  SupplierOffer,
  SupplierRfq,
} from "./types";
import { businessTypeLabel, statusLabel } from "../../localization/zh-CN";
import "./commercial.css";

const tabs: Array<{
  id: CommercialTab;
  label: string;
  icon: typeof IconUsers;
}> = [
  { id: "suppliers", label: "供应商", icon: IconUsers },
  { id: "rfqs", label: "供应商询价", icon: IconMailForward },
  { id: "supplier-quotes", label: "供应商报价", icon: IconFileDescription },
  { id: "quotes", label: "客户报价", icon: IconFileDescription },
  { id: "approvals", label: "审批工作台", icon: IconShieldCheck },
];
const emptyWorkspace: CommercialWorkspaceData = {
  workspace: "当前工作空间",
  workspaceType: "CHEMICAL_REAGENT",
  suppliers: [],
  rfqs: [],
  offers: [],
  quotes: [],
  approvals: [],
  metrics: { suppliers: 0, openRfqs: 0, quotes: 0, waitingApprovals: 0 },
  permissions: {
    supplierRead: false,
    supplierManage: false,
    rfqRead: false,
    supplierQuoteRead: false,
    customerQuoteRead: false,
    approvalRead: false,
    cost: false,
    margin: false,
    quoteApprove: false,
  },
};
const money = (value: number, currency: string) =>
  new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
const typeLabel = businessTypeLabel;
const statusTone = (status: string) =>
  ["APPROVED", "RESPONDED", "READY"].includes(status)
    ? "positive"
    : ["REJECTED", "EXPIRED", "OVERDUE"].includes(status)
      ? "danger"
      : ["WAITING_APPROVAL", "WAITING", "CHANGES_REQUESTED"].includes(status)
        ? "warning"
        : "neutral";

function Badge({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: string;
}) {
  return <span className={`commercial-badge ${tone}`}>{children}</span>;
}
function Empty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="commercial-empty">
      <IconCube size={24} />
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const close = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  useEffect(() => {
    close.current?.focus();
    const handle = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog.current) return;
      const nodes = [
        ...dialog.current.querySelectorAll<HTMLElement>(
          "button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]",
        ),
      ];
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);
  return (
    <div
      className="commercial-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialog}
        className="commercial-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="commercial-modal-title"
      >
        <header>
          <div>
            <span className="commercial-kicker">CRM 核心服务</span>
            <h2 id="commercial-modal-title">{title}</h2>
          </div>
          <button
            ref={close}
            type="button"
            className="icon-button"
            aria-label="关闭"
            onClick={onClose}
          >
            <IconX size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function SupplierPanel({
  data,
  onSelect,
  onCreate,
  onEdit,
}: {
  data: CommercialWorkspaceData;
  onSelect: (supplier: Supplier) => void;
  onCreate: () => void;
  onEdit: (supplier: Supplier) => void;
}) {
  const [query, setQuery] = useState("");
  const [risk, setRisk] = useState("ALL");
  const items = useMemo(
    () =>
      data.suppliers.filter(
        (item) =>
          (!query ||
            `${item.name} ${item.domain ?? ""}`
              .toLowerCase()
              .includes(query.toLowerCase())) &&
          (risk === "ALL" || item.risk === risk),
      ),
    [data.suppliers, query, risk],
  );
  return (
    <section className="commercial-panel">
      <div className="commercial-panel-heading">
        <div>
          <span className="commercial-kicker">供应商目录</span>
          <h2>供应商管理</h2>
          <p>供应商档案全局共享，不受当前工作空间和创建人限制。</p>
        </div>
        <div className="commercial-heading-stat">
          <strong>{data.metrics.suppliers}</strong>
          <span>个活跃供应商</span>
        </div>
      </div>
      <div className="commercial-toolbar">
        <label className="commercial-search">
          <IconSearch size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索供应商名称或域名"
            aria-label="搜索供应商"
          />
        </label>
        <select
          value={risk}
          onChange={(event) => setRisk(event.target.value)}
          aria-label="风险等级"
        >
          <option value="ALL">全部风险</option>
          <option value="LOW">低</option>
          <option value="MEDIUM">中</option>
          <option value="HIGH">高</option>
          <option value="UNASSESSED">未评估</option>
        </select>
        {data.permissions.supplierManage ? (
          <button
            type="button"
            className="commercial-primary"
            onClick={onCreate}
          >
            <IconPlus size={14} /> 新增供应商
          </button>
        ) : null}
      </div>
      <div className="supplier-table-scroll">
        <table className="supplier-table">
          <thead>
            <tr>
              <th>供应商名称</th>
              <th>域名</th>
              <th>创建时间</th>
              <th>风险</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>
                  <strong>{item.name}</strong>
                </td>
                <td>{item.domain ?? "—"}</td>
                <td>{new Date(item.createdAt).toLocaleString("zh-CN")}</td>
                <td>
                  <Badge
                    tone={
                      item.risk === "HIGH"
                        ? "danger"
                        : item.risk === "MEDIUM"
                          ? "warning"
                          : item.risk === "LOW"
                            ? "positive"
                            : "neutral"
                    }
                  >
                    {statusLabel(item.risk)}
                  </Badge>
                </td>
                <td>
                  <div className="supplier-row-actions">
                    <button
                      type="button"
                      className="commercial-secondary supplier-view-action"
                      onClick={() => onSelect(item)}
                    >
                      查看
                    </button>
                    {data.permissions.supplierManage ? (
                      <button
                        type="button"
                        className="commercial-secondary supplier-view-action"
                        onClick={() => onEdit(item)}
                      >
                        <IconPencil size={13} /> 修改
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!items.length ? (
        <Empty title="没有匹配的供应商" detail="调整搜索或筛选条件。" />
      ) : null}
    </section>
  );
}

function SupplierCreateDrawer({
  busy,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: CreateSupplierRequest) => void;
}) {
  const [form, setForm] = useState<CreateSupplierRequest>({
    displayName: "",
    countryCode: "CN",
    defaultCurrency: "CNY",
    supplierType: "CHEMICAL_REAGENT",
    riskLevel: "LOW",
    qualityStatus: "APPROVED",
    complianceStatus: "UNREVIEWED",
    leadTimeProfile: "14 days",
  });
  const update = (key: keyof CreateSupplierRequest, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };
  const clean = (): CreateSupplierRequest => {
    const entries = Object.entries(form)
      .map(([key, value]) => [key, value.trim()])
      .filter(([, value]) => value.length > 0);
    return Object.fromEntries(entries) as unknown as CreateSupplierRequest;
  };
  const canSubmit = form.displayName.trim();
  return (
    <Modal title="新增供应商" onClose={onClose}>
      <form
        className="commercial-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit && !busy) onSubmit(clean());
        }}
      >
        <label>
          <span>供应商名称</span>
          <input
            value={form.displayName}
            onChange={(event) => update("displayName", event.target.value)}
            placeholder="供应商展示名称"
            autoFocus
          />
        </label>
        <label>
          <span>域名（选填）</span>
          <input
            aria-label="域名（选填）"
            value={form.domain ?? ""}
            onChange={(event) => update("domain", event.target.value)}
            placeholder="例如 supplier.com"
          />
          <small>只填写域名；系统会去掉 www. 并转为小写。</small>
        </label>
        <label>
          <span>国家/地区</span>
          <input
            value={form.countryCode}
            onChange={(event) => update("countryCode", event.target.value)}
            maxLength={2}
          />
        </label>
        <label>
          <span>默认币种</span>
          <select
            value={form.defaultCurrency}
            onChange={(event) => update("defaultCurrency", event.target.value)}
          >
            <option value="CNY">CNY</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </label>
        <label>
          <span>供应商类型</span>
          <select
            value={form.supplierType}
            onChange={(event) => update("supplierType", event.target.value)}
          >
            <option value="CHEMICAL_REAGENT">化学试剂</option>
            <option value="GENERAL">通用供应商</option>
          </select>
        </label>
        <label>
          <span>风险等级</span>
          <select
            value={form.riskLevel}
            onChange={(event) => update("riskLevel", event.target.value)}
          >
            <option value="LOW">低</option>
            <option value="MEDIUM">中</option>
            <option value="HIGH">高</option>
            <option value="UNASSESSED">未评估</option>
          </select>
        </label>
        <label>
          <span>资质状态</span>
          <select
            value={form.qualityStatus}
            onChange={(event) => update("qualityStatus", event.target.value)}
          >
            <option value="APPROVED">已通过</option>
            <option value="UNREVIEWED">未复核</option>
          </select>
        </label>
        <label>
          <span>交期说明</span>
          <input
            value={form.leadTimeProfile}
            onChange={(event) => update("leadTimeProfile", event.target.value)}
            placeholder="例如 14 days"
          />
        </label>
        <label className="is-wide">
          <span>网站（选填）</span>
          <input
            aria-label="网站（选填）"
            value={form.website ?? ""}
            onChange={(event) => update("website", event.target.value)}
            placeholder="https://example.com"
          />
        </label>
        <div className="decision-actions">
          <button
            type="button"
            className="commercial-secondary"
            disabled={busy}
            onClick={onClose}
          >
            取消
          </button>
          <button
            type="submit"
            className="commercial-primary"
            disabled={busy || !canSubmit}
          >
            {busy ? "保存中…" : "保存供应商"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SupplierEditDrawer({
  supplier,
  busy,
  onClose,
  onSubmit,
}: {
  supplier: Supplier;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: UpdateSupplierRequest) => void;
}) {
  const relationship = supplier.workspaceRelationship;
  const [form, setForm] = useState({
    displayName: supplier.name,
    domain: supplier.domain ?? "",
    website: supplier.website ?? "",
    countryCode: supplier.country === "UNSPECIFIED" ? "" : supplier.country,
    defaultCurrency: relationship?.defaultCurrency ?? "USD",
    supplierType: relationship?.supplierType ?? "GENERAL",
    riskLevel: relationship?.riskLevel ?? "UNASSESSED",
    qualityStatus: relationship?.qualityStatus ?? "UNREVIEWED",
    leadTimeProfile: relationship?.leadTimeProfile ?? "",
  });
  const update = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };
  const submit = () => {
    const input: UpdateSupplierRequest = {
      displayName: form.displayName.trim(),
      domain: form.domain.trim() || null,
      website: form.website.trim() || null,
      countryCode: form.countryCode.trim().toUpperCase() || null,
    };
    if (relationship) {
      input.defaultCurrency = form.defaultCurrency;
      input.supplierType = form.supplierType;
      input.riskLevel = form.riskLevel;
      input.qualityStatus = form.qualityStatus;
      input.leadTimeProfile = form.leadTimeProfile;
    }
    onSubmit(input);
  };
  const canSubmit = Boolean(form.displayName.trim());

  return (
    <Modal title="修改供应商" onClose={onClose}>
      <form
        className="commercial-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit && !busy) submit();
        }}
      >
        <label>
          <span>供应商名称</span>
          <input
            value={form.displayName}
            onChange={(event) => update("displayName", event.target.value)}
            autoFocus
          />
        </label>
        <label>
          <span>域名（选填）</span>
          <input
            aria-label="域名（选填）"
            value={form.domain}
            onChange={(event) => update("domain", event.target.value)}
            placeholder="例如 supplier.com"
          />
          <small>只填写域名；系统会去掉 www. 并转为小写。</small>
        </label>
        <label>
          <span>国家/地区（选填）</span>
          <input
            value={form.countryCode}
            onChange={(event) => update("countryCode", event.target.value)}
            maxLength={2}
            placeholder="例如 CN"
          />
        </label>
        <label>
          <span>网站（选填）</span>
          <input
            aria-label="网站（选填）"
            value={form.website}
            onChange={(event) => update("website", event.target.value)}
            placeholder="https://example.com"
          />
        </label>
        {relationship ? (
          <>
            <label>
              <span>默认币种</span>
              <select
                value={form.defaultCurrency}
                onChange={(event) => update("defaultCurrency", event.target.value)}
              >
                <option value="CNY">CNY</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
            <label>
              <span>供应商类型</span>
              <select
                value={form.supplierType}
                onChange={(event) => update("supplierType", event.target.value)}
              >
                <option value="CHEMICAL_REAGENT">化学试剂</option>
                <option value="EMBODIED_INTELLIGENCE">具身智能</option>
                <option value="GENERAL">通用供应商</option>
              </select>
            </label>
            <label>
              <span>风险等级</span>
              <select
                value={form.riskLevel}
                onChange={(event) => update("riskLevel", event.target.value)}
              >
                <option value="LOW">低</option>
                <option value="MEDIUM">中</option>
                <option value="HIGH">高</option>
                <option value="UNASSESSED">未评估</option>
              </select>
            </label>
            <label>
              <span>资质状态</span>
              <select
                value={form.qualityStatus}
                onChange={(event) => update("qualityStatus", event.target.value)}
              >
                <option value="APPROVED">已通过</option>
                <option value="UNREVIEWED">未复核</option>
              </select>
            </label>
            <label className="is-wide">
              <span>交期说明</span>
              <input
                value={form.leadTimeProfile}
                onChange={(event) => update("leadTimeProfile", event.target.value)}
              />
            </label>
          </>
        ) : (
          <p className="commercial-form-note is-wide">
            当前工作空间没有该供应商的业务关系；本次只修改全局档案。
          </p>
        )}
        <div className="decision-actions">
          <button
            type="button"
            className="commercial-secondary"
            disabled={busy}
            onClick={onClose}
          >
            取消
          </button>
          <button
            type="submit"
            className="commercial-primary"
            disabled={busy || !canSubmit}
          >
            {busy ? "保存中…" : "保存修改"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SupplierDrawer({
  supplier,
  onClose,
  onEdit,
}: {
  supplier: Supplier;
  onClose: () => void;
  onEdit?: () => void;
}) {
  return (
    <Modal title={supplier.name} onClose={onClose}>
      <div className="drawer-business">
        <Badge
          tone={
            supplier.risk === "HIGH"
              ? "danger"
              : supplier.risk === "MEDIUM"
                ? "warning"
                : supplier.risk === "LOW"
                  ? "positive"
                  : "neutral"
          }
        >
          {statusLabel(supplier.risk)}风险
        </Badge>
        <span>{typeLabel(supplier.type)}</span>
        <span>
          {supplier.country} · {supplier.currency}
        </span>
      </div>
      <div className="commercial-dl">
        <div>
          <dt>域名</dt>
          <dd>{supplier.domain ?? "—"}</dd>
        </div>
        <div>
          <dt>网站</dt>
          <dd>{supplier.website ?? "—"}</dd>
        </div>
        <div>
          <dt>创建时间</dt>
          <dd>{new Date(supplier.createdAt).toLocaleString("zh-CN")}</dd>
        </div>
        <div>
          <dt>资质状态</dt>
          <dd>{statusLabel(supplier.quality)}</dd>
        </div>
        <div>
          <dt>交期信息</dt>
          <dd>{supplier.leadTime}</dd>
        </div>
        <div>
          <dt>联系人</dt>
          <dd>{supplier.contacts.length}</dd>
        </div>
      </div>
      <h3 className="commercial-subheading">能力快照</h3>
      <div className="capability-chips large">
        {supplier.capabilities.map((item) => (
          <span key={item}>{item}</span>
        ))}
      </div>
      <h3 className="commercial-subheading">供应商联系人</h3>
      <div className="contact-list">
        {supplier.contacts.map((contact) => (
          <div key={contact.email}>
            <IconUsers size={15} />
            <span>
              <strong>{contact.name}</strong>
              <small>
                {contact.role} · {contact.language} · {contact.email}
              </small>
            </span>
          </div>
        ))}
      </div>
      {onEdit ? (
        <div className="drawer-actions">
          <button type="button" className="commercial-primary" onClick={onEdit}>
            <IconPencil size={14} /> 修改供应商
          </button>
        </div>
      ) : null}
    </Modal>
  );
}

function RfqPanel({
  data,
  onSelect,
  onSend,
}: {
  data: CommercialWorkspaceData;
  onSelect: (rfq: SupplierRfq) => void;
  onSend: (rfq: SupplierRfq) => void;
}) {
  const [filter, setFilter] = useState("ALL");
  const items = data.rfqs.filter(
    (item) => filter === "ALL" || item.status === filter,
  );
  return (
    <section className="commercial-panel">
      <div className="commercial-panel-heading">
        <div>
          <span className="commercial-kicker">供应商询价</span>
          <h2>询价运营</h2>
          <p>所有发送均为仅测试的合成发送；不会调用 Gmail 或联系真实供应商。</p>
        </div>
        <div className="commercial-heading-stat">
          <strong>{data.metrics.openRfqs}</strong>
          <span>个待处理 RFQ</span>
        </div>
      </div>
      <div className="commercial-toolbar">
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="RFQ 状态"
        >
          <option value="ALL">全部状态</option>
          <option value="READY">就绪</option>
          <option value="FAKE_SENT">已合成发送</option>
          <option value="RESPONDED">已回复</option>
          <option value="EXPIRED">已过期</option>
        </select>
        <span className="toolbar-hint">
          <IconClock size={14} /> 回复截止日期受工作空间范围约束
        </span>
      </div>
      <div className="rfq-list">
        {items.map((rfq) => (
          <article className="rfq-row" key={rfq.id}>
            <button
              type="button"
              className="rfq-main"
              onClick={() => onSelect(rfq)}
            >
              <span className="rfq-number">{rfq.number}</span>
              <span className="rfq-supplier">
                <strong>{rfq.supplierName}</strong>
                <small>
                  {typeLabel(rfq.businessType)} · {rfq.lines.length} 个明细项
                </small>
              </span>
              <span>
                <Badge tone={statusTone(rfq.status)}>
                  {statusLabel(rfq.status)}
                </Badge>
                <small className="rfq-due">截止 {rfq.due}</small>
              </span>
              <span className="rfq-response">
                {rfq.responses}/{rfq.lines.length} 个回复
              </span>
              <IconArrowRight size={16} />
            </button>
            {["READY", "DRAFT"].includes(rfq.status) ? (
              <button
                type="button"
                className="text-action"
                onClick={() => onSend(rfq)}
              >
                <IconMailForward size={14} /> 合成发送
              </button>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function RfqDrawer({
  rfq,
  onClose,
  onSend,
}: {
  rfq: SupplierRfq;
  onClose: () => void;
  onSend: () => void;
}) {
  return (
    <Modal title={rfq.number} onClose={onClose}>
      <div className="drawer-business">
        <Badge tone={statusTone(rfq.status)}>{statusLabel(rfq.status)}</Badge>
        <span>{rfq.supplierName}</span>
        <span>{typeLabel(rfq.businessType)}</span>
      </div>
      <div className="commercial-dl">
        <div>
          <dt>回复截止日期</dt>
          <dd>{rfq.due}</dd>
        </div>
        <div>
          <dt>币种</dt>
          <dd>{rfq.currency}</dd>
        </div>
        <div>
          <dt>回复数</dt>
          <dd>
            {rfq.responses} / {rfq.lines.length}
          </dd>
        </div>
      </div>
      <div className="drawer-actions">
        {["READY", "DRAFT"].includes(rfq.status) ? (
          <button type="button" className="commercial-primary" onClick={onSend}>
            <IconMailForward size={15} /> 合成发送
          </button>
        ) : null}
        <Badge tone="neutral">不会发送真实邮件</Badge>
      </div>
      <h3 className="commercial-subheading">RFQ 明细项</h3>
      <div className="line-list">
        {rfq.lines.map((line) => (
          <div className="line-card" key={line.id}>
            <strong>{line.product}</strong>
            <span>
              {line.description} · {line.quantity}
            </span>
            <small>
              {Object.entries(line.specification)
                .map(([key, value]) => `${key}: ${value}`)
                .join(" · ")}
            </small>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function SupplierQuotePanel({ data }: { data: CommercialWorkspaceData }) {
  return (
    <section className="commercial-panel">
      <div className="commercial-panel-heading">
        <div>
          <span className="commercial-kicker">供应商报价</span>
          <h2>供应商报价比较</h2>
          <p>报价快照来自核心服务；成本仅在具备独立权限时显示。</p>
        </div>
        <div className="commercial-heading-stat">
          <strong>{data.offers.length}</strong>
          <span>份已收报价</span>
        </div>
      </div>
      {!data.offers.length ? (
        <Empty
          title="暂无供应商报价"
          detail="当前工作空间没有可读取的报价快照。"
        />
      ) : (
        <div className="rfq-list">
          {data.offers.map((offer) => (
            <article className="rfq-row" key={offer.id}>
              <div className="rfq-main supplier-quote-row">
                <span className="rfq-number">{offer.rfqNumber}</span>
                <span className="rfq-supplier">
                  <strong>{offer.supplierName}</strong>
                  <small>
                    {typeLabel(offer.businessType)} · {offer.leadTime}
                  </small>
                </span>
                <span>
                  <Badge tone="neutral">{offer.currency}</Badge>
                  <small className="rfq-due">有效期 {offer.validity}</small>
                </span>
                <span className="rfq-response">
                  {data.permissions.cost
                    ? money(offer.landedCost, offer.currency)
                    : "成本受限"}
                </span>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function QuotePanel({
  data,
  onSelect,
}: {
  data: CommercialWorkspaceData;
  onSelect: (quote: CustomerQuote) => void;
}) {
  const [filter, setFilter] = useState("ALL");
  const items = data.quotes.filter(
    (item) => filter === "ALL" || item.status === filter,
  );
  return (
    <section className="commercial-panel">
      <div className="commercial-panel-heading">
        <div>
          <span className="commercial-kicker">客户报价</span>
          <h2>客户报价与版本</h2>
          <p>报价事实来自核心服务；供应商成本与毛利根据权限分别显示。</p>
        </div>
        <div className="commercial-heading-stat">
          <strong>{data.metrics.quotes}</strong>
          <span>个报价版本</span>
        </div>
      </div>
      <div className="commercial-toolbar">
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="报价状态"
        >
          <option value="ALL">全部状态</option>
          <option value="APPROVED">已批准</option>
          <option value="WAITING_APPROVAL">等待审批</option>
          <option value="CHANGES_REQUESTED">已要求修改</option>
          <option value="EXPIRED">已过期</option>
        </select>
        <span className="permission-pill">
          <IconLock size={13} />{" "}
          {data.permissions.cost ? "已获成本查看权限" : "成本已隐藏"}
        </span>
        <span className="permission-pill">
          <IconLock size={13} />{" "}
          {data.permissions.margin ? "已获毛利查看权限" : "毛利已隐藏"}
        </span>
      </div>
      <div className="quote-grid">
        {items.map((quote) => (
          <button
            type="button"
            key={quote.id}
            className="quote-card"
            onClick={() => onSelect(quote)}
          >
            <div className="quote-card-top">
              <span>
                <strong>{quote.number}</strong>
                <small>{quote.customer}</small>
              </span>
              <Badge tone={statusTone(quote.status)}>
                {statusLabel(quote.status)}
              </Badge>
            </div>
            <div className="quote-amounts">
              <span>
                <small>总额</small>
                <strong>{money(quote.total, quote.currency)}</strong>
              </span>
              {data.permissions.cost ? (
                <span>
                  <small>成本快照</small>
                  <strong>{money(quote.totalCost, quote.currency)}</strong>
                </span>
              ) : (
                <span className="redacted">
                  <small>成本快照</small>
                  <strong>受限</strong>
                </span>
              )}
              {data.permissions.margin ? (
                <span>
                  <small>毛利</small>
                  <strong>{(quote.marginBps / 100).toFixed(1)}%</strong>
                </span>
              ) : (
                <span className="redacted">
                  <small>毛利</small>
                  <strong>受限</strong>
                </span>
              )}
            </div>
            <div className="quote-card-foot">
              <span>
                v{quote.version} · {quote.currency}
              </span>
              <span>{quote.terms}</span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function QuoteDrawer({
  quote,
  data,
  onClose,
}: {
  quote: CustomerQuote;
  data: CommercialWorkspaceData;
  onClose: () => void;
}) {
  return (
    <Modal title={`${quote.number} · v${quote.version}`} onClose={onClose}>
      <div className="drawer-business">
        <Badge tone={statusTone(quote.status)}>
          {statusLabel(quote.status)}
        </Badge>
        <span>{quote.customer}</span>
        <span>{typeLabel(quote.businessType)}</span>
      </div>
      <div className="commercial-dl">
        <div>
          <dt>客户报价总额</dt>
          <dd>{money(quote.total, quote.currency)}</dd>
        </div>
        <div>
          <dt>有效期至</dt>
          <dd>2026-12-31</dd>
        </div>
        <div>
          <dt>条款</dt>
          <dd>{quote.terms}</dd>
        </div>
      </div>
      <h3 className="commercial-subheading">报价构建器 · 不可变修订版本</h3>
      <div className="quote-lines">
        {quote.lines.map((line) => (
          <div className="quote-line" key={line.product}>
            <span>
              <strong>{line.product}</strong>
              <small>
                {line.quantity} · {line.leadTime}
              </small>
            </span>
            <strong>{money(line.price, quote.currency)}</strong>
            <span className="snapshot-ref">
              <IconLock size={12} />{" "}
              {data.permissions.cost ? line.costSnapshot : "成本快照受限"}
            </span>
          </div>
        ))}
      </div>
      {data.permissions.margin ? (
        <div className="margin-callout">
          <span>毛利率</span>
          <strong>{(quote.marginBps / 100).toFixed(1)}%</strong>
          <small>毛利仅用于辅助展示；持久化事实由核心服务管理。</small>
        </div>
      ) : (
        <div className="redacted-callout">
          <IconLock size={15} /> 毛利计算仅对获授权的复核人员开放。
        </div>
      )}
      <div className="drawer-actions">
        <Badge tone="neutral">不会向真实客户发送</Badge>
        <span className="drawer-note">
          修订会创建新的不可变版本，并重新执行审批。
        </span>
      </div>
    </Modal>
  );
}

function ApprovalPanel({
  data,
  onSelect,
}: {
  data: CommercialWorkspaceData;
  onSelect: (approval: ApprovalRequest) => void;
}) {
  const [filter, setFilter] = useState("WAITING");
  const items = data.approvals.filter(
    (item) => filter === "ALL" || item.status === filter,
  );
  return (
    <section className="commercial-panel">
      <div className="commercial-panel-heading">
        <div>
          <span className="commercial-kicker">审批工作台</span>
          <h2>审批与风险</h2>
          <p>审批人、步骤和决定由核心服务状态机与审计事实驱动。</p>
        </div>
        <div className="commercial-heading-stat">
          <strong>{data.metrics.waitingApprovals}</strong>
          <span>项待决定</span>
        </div>
      </div>
      <div className="approval-summary">
        <div>
          <IconAlertTriangle size={16} />
          <span>低毛利 / 特殊账期必须审批</span>
        </div>
        <div>
          <IconShieldCheck size={16} />
          <span>请求人不能审批自己的报价</span>
        </div>
        <div>
          <IconLock size={16} />
          <span>批准前禁止进入待发送状态</span>
        </div>
      </div>
      <div className="commercial-toolbar">
        <select
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          aria-label="审批状态"
        >
          <option value="WAITING">等待中</option>
          <option value="CHANGES_REQUESTED">已要求修改</option>
          <option value="APPROVED">已批准</option>
          <option value="REJECTED">已拒绝</option>
          <option value="OVERDUE">已逾期</option>
          <option value="ALL">全部状态</option>
        </select>
      </div>
      <div className="approval-list">
        {items.map((approval) => (
          <button
            type="button"
            key={approval.id}
            className="approval-row"
            onClick={() => onSelect(approval)}
          >
            <span className="approval-icon">
              <IconShieldCheck size={17} />
            </span>
            <span className="approval-main">
              <strong>
                {approval.quoteNumber} · {approval.customer}
              </strong>
              <small>{approval.summary}</small>
              <span className="approval-tags">
                {approval.types.map((type) => (
                  <Badge key={type} tone="neutral">
                    {statusLabel(type)}
                  </Badge>
                ))}
              </span>
            </span>
            <span className="approval-step">
              <strong>
                {approval.currentStep}/{approval.totalSteps}
              </strong>
              <small>步骤</small>
            </span>
            <span>
              <Badge tone={statusTone(approval.status)}>
                {statusLabel(approval.status)}
              </Badge>
              <small className="rfq-due">截止 {approval.due}</small>
            </span>
            <IconArrowRight size={16} />
          </button>
        ))}
      </div>
      {!items.length ? (
        <Empty
          title="当前审批视图为空"
          detail="切换等待中、已批准或已逾期状态。"
        />
      ) : null}
    </section>
  );
}

function ApprovalDrawer({
  approval,
  onClose,
  onDecision,
  canDecide,
}: {
  approval: ApprovalRequest;
  onClose: () => void;
  onDecision: (
    decision: "APPROVE" | "REJECT" | "REQUEST_CHANGES",
    reason: string,
  ) => void;
  canDecide: boolean;
}) {
  const [reason, setReason] = useState("");
  const disabled = !canDecide || reason.trim().length < 3;
  return (
    <Modal title={`${approval.quoteNumber} · 审批`} onClose={onClose}>
      <div className="drawer-business">
        <Badge tone={statusTone(approval.status)}>
          {statusLabel(approval.status)}
        </Badge>
        <span>{approval.customer}</span>
        <span>{typeLabel(approval.businessType)}</span>
      </div>
      <div className="commercial-dl">
        <div>
          <dt>申请人</dt>
          <dd>{approval.requester}</dd>
        </div>
        <div>
          <dt>当前步骤</dt>
          <dd>
            {approval.currentStep} / {approval.totalSteps}
          </dd>
        </div>
        <div>
          <dt>截止日期</dt>
          <dd>{approval.due}</dd>
        </div>
      </div>
      <div className="approval-detail-summary">
        <IconAlertTriangle size={17} />
        <p>{approval.summary}</p>
      </div>
      <h3 className="commercial-subheading">审批决定</h3>
      <textarea
        aria-label="审批意见"
        placeholder="填写决定原因（必填）"
        rows={3}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      <div className="decision-actions">
        <button
          type="button"
          className="commercial-secondary"
          disabled={disabled}
          onClick={() => onDecision("REQUEST_CHANGES", reason.trim())}
        >
          要求修改
        </button>
        <button
          type="button"
          className="commercial-secondary danger"
          disabled={disabled}
          onClick={() => onDecision("REJECT", reason.trim())}
        >
          拒绝
        </button>
        <button
          type="button"
          className="commercial-primary"
          disabled={disabled}
          onClick={() => onDecision("APPROVE", reason.trim())}
        >
          <IconCheck size={15} /> 批准
        </button>
      </div>
      <p className="drawer-note">
        <IconLock size={12} /> 决定会带 expectedVersion、CSRF、幂等键并写入
        审计记录；此演示不会发送报价。
      </p>
      {!canDecide ? (
        <p className="drawer-note">
          当前角色只有审批读取权限，决定操作已禁用。
        </p>
      ) : null}
    </Modal>
  );
}

export default function CommercialDevelopmentApp({
  tab: controlledTab,
  embedded = false,
  onTabChange,
}: {
  tab?: CommercialTab;
  embedded?: boolean;
  onTabChange?: (tab: CommercialTab) => void;
}) {
  const [data, setData] = useState<CommercialWorkspaceData>(emptyWorkspace);
  const [internalTab, setInternalTab] = useState<CommercialTab>("suppliers");
  const tab = controlledTab ?? internalTab;
  const [supplier, setSupplier] = useState<Supplier>();
  const [editingSupplier, setEditingSupplier] = useState<Supplier>();
  const [rfq, setRfq] = useState<SupplierRfq>();
  const [quote, setQuote] = useState<CustomerQuote>();
  const [approval, setApproval] = useState<ApprovalRequest>();
  const [notice, setNotice] = useState("正在连接 CRM 核心服务…");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [decisionBusy, setDecisionBusy] = useState(false);
  const [supplierCreateOpen, setSupplierCreateOpen] = useState(false);
  const [supplierCreateBusy, setSupplierCreateBusy] = useState(false);
  const [supplierEditBusy, setSupplierEditBusy] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await loadCommercialWorkspace();
      setData(result);
      setNotice("CRM 核心服务已连接 · 供应商目录全局共享，业务记录按工作空间隔离");
    } catch (cause) {
      setData(emptyWorkspace);
      setError(describeCommercialError(cause));
      setNotice("CRM 核心服务请求失败 · 未使用本地回退数据");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const selectTab = (next: CommercialTab) => {
    setInternalTab(next);
    onTabChange?.(next);
  };
  const handleSend = async (item: SupplierRfq) => {
    setError("");
    try {
      await syntheticSend(`/supplier-rfqs/${item.id}/synthetic-send`);
      await load();
      setNotice(
        `核心服务已记录 ${item.number} 的仅测试发送；未调用真实服务商。`,
      );
    } catch (cause) {
      setError(describeCommercialError(cause));
    }
  };
  const handleDecision = async (
    decision: "APPROVE" | "REJECT" | "REQUEST_CHANGES",
    reason: string,
  ) => {
    if (!approval) return;
    setDecisionBusy(true);
    setError("");
    try {
      await decideApproval(
        approval.id,
        approval.expectedVersion,
        decision,
        reason,
      );
      setApproval(undefined);
      await load();
      setNotice(`${statusLabel(decision)}已由 CRM 核心服务确认。`);
    } catch (cause) {
      setError(describeCommercialError(cause));
    } finally {
      setDecisionBusy(false);
    }
  };
  const handleCreateSupplier = async (input: CreateSupplierRequest) => {
    setSupplierCreateBusy(true);
    setError("");
    try {
      await createSupplier(input);
      setSupplierCreateOpen(false);
      await load();
      setNotice(`供应商 ${input.displayName} 已新增。`);
    } catch (cause) {
      setError(describeCommercialError(cause));
    } finally {
      setSupplierCreateBusy(false);
    }
  };
  const handleUpdateSupplier = async (input: UpdateSupplierRequest) => {
    if (!editingSupplier) return;
    setSupplierEditBusy(true);
    setError("");
    try {
      await updateSupplier(editingSupplier.id, input);
      setEditingSupplier(undefined);
      setSupplier(undefined);
      await load();
      setNotice(`供应商 ${input.displayName} 已更新。`);
    } catch (cause) {
      setError(describeCommercialError(cause));
    } finally {
      setSupplierEditBusy(false);
    }
  };
  return (
    <div
      className={
        embedded ? "commercial-dev-shell is-embedded" : "commercial-dev-shell"
      }
    >
      {!embedded ? (
        <header className="commercial-topbar">
          <div className="commercial-brand">
            <span className="commercial-mark">C</span>
            <span>
              <strong>商业运营</strong>
              <small>CRM-INT-26A · 核心 API</small>
            </span>
          </div>
          <div className="commercial-top-actions">
            <Badge tone="neutral">
              <IconFlask size={13} /> P0 合成数据
            </Badge>
            <button
              type="button"
              className="commercial-ghost"
              onClick={() => setShowPermissions((value) => !value)}
            >
              <IconLock size={14} /> 权限
            </button>
            <button
              type="button"
              className="commercial-ghost"
              onClick={() => void load()}
            >
              <IconRefresh size={14} /> 刷新
            </button>
          </div>
        </header>
      ) : null}
      <div className="commercial-layout">
        {!embedded ? (
          <aside className="commercial-sidebar" aria-label="商业运营导航">
            <div className="commercial-context">
              <span>工作空间</span>
              <strong>{data.workspace}</strong>
              <small>业务记录按工作空间隔离</small>
            </div>
            <nav>
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={
                    tab === id ? "commercial-nav active" : "commercial-nav"
                  }
                  onClick={() => selectTab(id)}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                  {id === "approvals" ? (
                    <b>{data.metrics.waitingApprovals}</b>
                  ) : null}
                </button>
              ))}
            </nav>
            <div className="commercial-sidebar-foot">
              <IconShieldCheck size={14} />
              <span>
                已启用审计
                <br />
                已启用成本 / 毛利边界
              </span>
            </div>
          </aside>
        ) : null}
        <main className="commercial-main">
          <div className="commercial-notice">
            <span className="notice-dot" />
            {notice}
          </div>
          {error ? (
            <div className="commercial-notice is-error" role="alert">
              <IconAlertTriangle size={15} /> {error}
            </div>
          ) : null}
          {showPermissions ? (
            <div className="permission-popover" role="status">
              <strong>当前生效权限</strong>
              <span>
                <IconCheck size={13} /> supplier.read
              </span>
              <span
                className={data.permissions.supplierManage ? "" : "restricted"}
              >
                {data.permissions.supplierManage ? (
                  <IconCheck size={13} />
                ) : (
                  <IconLock size={13} />
                )}{" "}
                supplier.manage
              </span>
              <span>
                <IconCheck size={13} /> customer.quote.read
              </span>
              <span className={data.permissions.cost ? "" : "restricted"}>
                {data.permissions.cost ? (
                  <IconCheck size={13} />
                ) : (
                  <IconLock size={13} />
                )}{" "}
                supplier.cost.read
              </span>
              <span className={data.permissions.margin ? "" : "restricted"}>
                {data.permissions.margin ? (
                  <IconCheck size={13} />
                ) : (
                  <IconLock size={13} />
                )}{" "}
                customer.quote.margin.read
              </span>
              <small>工作空间管理员默认无供应商成本权限。</small>
            </div>
          ) : null}
          {loading ? (
            <Empty
              title="正在读取商业数据"
              detail="等待 CRM 核心服务返回当前工作空间投影。"
            />
          ) : null}
          {!loading && tab === "suppliers" && data.permissions.supplierRead ? (
            <SupplierPanel
              data={data}
              onSelect={setSupplier}
              onCreate={() => setSupplierCreateOpen(true)}
              onEdit={setEditingSupplier}
            />
          ) : null}
          {!loading && tab === "rfqs" && data.permissions.rfqRead ? (
            <RfqPanel
              data={data}
              onSelect={setRfq}
              onSend={(item) => void handleSend(item)}
            />
          ) : null}
          {!loading &&
          tab === "supplier-quotes" &&
          data.permissions.supplierQuoteRead ? (
            <SupplierQuotePanel data={data} />
          ) : null}
          {!loading &&
          tab === "quotes" &&
          data.permissions.customerQuoteRead ? (
            <QuotePanel data={data} onSelect={setQuote} />
          ) : null}
          {!loading && tab === "approvals" && data.permissions.approvalRead ? (
            <ApprovalPanel data={data} onSelect={setApproval} />
          ) : null}
          {!loading &&
          ((tab === "suppliers" && !data.permissions.supplierRead) ||
            (tab === "rfqs" && !data.permissions.rfqRead) ||
            (tab === "supplier-quotes" &&
              !data.permissions.supplierQuoteRead) ||
            (tab === "quotes" && !data.permissions.customerQuoteRead) ||
            (tab === "approvals" && !data.permissions.approvalRead)) ? (
            <Empty
              title="权限不足"
              detail="当前工作空间角色没有该模块的读取权限。"
            />
          ) : null}
        </main>
      </div>
      {supplier ? (
        <SupplierDrawer
          supplier={supplier}
          onClose={() => setSupplier(undefined)}
          onEdit={
            data.permissions.supplierManage
              ? () => {
                  setEditingSupplier(supplier);
                  setSupplier(undefined);
                }
              : undefined
          }
        />
      ) : null}
      {supplierCreateOpen ? (
        <SupplierCreateDrawer
          busy={supplierCreateBusy}
          onClose={() => setSupplierCreateOpen(false)}
          onSubmit={(input) => void handleCreateSupplier(input)}
        />
      ) : null}
      {editingSupplier ? (
        <SupplierEditDrawer
          supplier={editingSupplier}
          busy={supplierEditBusy}
          onClose={() => setEditingSupplier(undefined)}
          onSubmit={(input) => void handleUpdateSupplier(input)}
        />
      ) : null}
      {rfq ? (
        <RfqDrawer
          rfq={rfq}
          onClose={() => setRfq(undefined)}
          onSend={() => void handleSend(rfq)}
        />
      ) : null}
      {quote ? (
        <QuoteDrawer
          quote={quote}
          data={data}
          onClose={() => setQuote(undefined)}
        />
      ) : null}
      {approval ? (
        <ApprovalDrawer
          approval={approval}
          onClose={() => setApproval(undefined)}
          onDecision={(decision, reason) =>
            void handleDecision(decision, reason)
          }
          canDecide={data.permissions.quoteApprove && !decisionBusy}
        />
      ) : null}
    </div>
  );
}

function describeCommercialError(cause: unknown): string {
  if (!(cause instanceof CommercialApiError))
    return "商业运营服务暂时不可用，请稍后重试。";
  if (cause.status === 401) return "会话已过期，请重新登录。";
  if (cause.status === 403) return "当前工作空间没有对应的商业运营权限。";
  if (cause.status === 409) return cause.detail ?? "记录已更新，请刷新后重试。";
  if (cause.status === 422) return "请求参数未通过校验。";
  return "商业运营服务暂时不可用，请稍后重试。";
}
